import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Info, CheckCircle, AlertTriangle, AlertCircle, BookOpen } from 'lucide-react';
import { STATUS } from '../../config/designTokens';
export type BannerVariant = 'info' | 'success' | 'warning' | 'danger' | 'assignment';
export interface BannerProps {
  variant?: BannerVariant;
  icon?: React.ElementType;
  title?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
  onDismiss?: () => void;
  className?: string;
}
export function Banner({
  variant = 'info',
  icon,
  title,
  children,
  action,
  onDismiss,
  className = ''
}: BannerProps) {
  const getVariantStyles = () => {
    switch (variant) {
      case 'success':
        return {
          container: `${STATUS.success.bgLight} border-${STATUS.success.border.split('-')[1]}-200`,
          text: STATUS.success.textDark,
          icon: STATUS.success.text,
          DefaultIcon: CheckCircle
        };
      case 'warning':
        return {
          container: `${STATUS.warning.bgLight} border-${STATUS.warning.border.split('-')[1]}-200`,
          text: STATUS.warning.textDark,
          icon: STATUS.warning.text,
          DefaultIcon: AlertTriangle
        };
      case 'danger':
        return {
          container: `${STATUS.danger.bgLight} border-${STATUS.danger.border.split('-')[1]}-200`,
          text: STATUS.danger.textDark,
          icon: STATUS.danger.text,
          DefaultIcon: AlertCircle
        };
      case 'assignment':
        return {
          container: `bg-indigo-50 border-indigo-200`,
          text: 'text-indigo-900',
          icon: 'text-indigo-600',
          DefaultIcon: BookOpen
        };
      case 'info':
      default:
        return {
          container: `${STATUS.info.bgLight} border-${STATUS.info.border.split('-')[1]}-200`,
          text: STATUS.info.textDark,
          icon: STATUS.info.text,
          DefaultIcon: Info
        };
    }
  };
  const styles = getVariantStyles();
  const Icon = icon || styles.DefaultIcon;
  return <AnimatePresence data-id="element-2426">
      <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      height: 0,
      marginBottom: 0
    }} className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-4 py-3 border-b sm:border rounded-none sm:rounded-2xl shadow-sm ${styles.container} ${className}`} role="alert" data-id="element-2427">
        <div className="flex items-start sm:items-center gap-3 flex-1" data-id="element-2428">
          <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 sm:mt-0 ${styles.icon}`} data-id="element-2429" />
          <div className={`text-sm ${styles.text}`} data-id="element-2430">
            {title && <span className="font-bold mr-2" data-id="element-2431">{title}</span>}
            {children}
          </div>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto mt-2 sm:mt-0 ml-8 sm:ml-0" data-id="element-2432">
          {action && <div className="flex-shrink-0" data-id="element-2433">{action}</div>}
          {onDismiss && <button onClick={onDismiss} className={`p-1.5 rounded-lg hover:bg-black/5 transition-colors focus:outline-none focus:ring-2 focus:ring-black/10 ${styles.icon}`} aria-label="Sluiten" data-id="element-2434">
              <X className="w-4 h-4" data-id="element-2435" />
            </button>}
        </div>
      </motion.div>
    </AnimatePresence>;
}